// src/components/Header.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Button,
  Icon,
  IconButton,
  Image,
  Text,
  HStack,
  Tooltip,
  useDisclosure,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaSignOutAlt, FaUser, FaBars } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import ChangePasswordModal from './ChangePasswordModal';

const CACHE_KEY = 'nbu_rates_cache';

const fetchRates = async () => {
  try {
    const res = await fetch('https://bank.gov.ua/NBUStatService/v1/statdirectory/exchange?json');
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const data = await res.json();
    const usd = data.find(r => r.cc === 'USD');
    const eur = data.find(r => r.cc === 'EUR');
    return {
      usd: usd ? usd.rate.toFixed(2) : null,
      eur: eur ? eur.rate.toFixed(2) : null,
      date: usd?.exchangedate || null,
    };
  } catch {
    return { usd: null, eur: null, date: null };
  }
};

const Header = ({ onMenuClick, showMenuButton }) => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [rates, setRates] = useState({ usd: null, eur: null, date: null });
  const { isOpen, onOpen, onClose } = useDisclosure();

  const rateColor = useColorModeValue('whiteAlpha.900', 'whiteAlpha.900');
  const rateLabelColor = useColorModeValue('whiteAlpha.700', 'whiteAlpha.600');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch once per browser session; reuse on navigation
  useEffect(() => {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      try { setRates(JSON.parse(cached)); return; } catch {}
    }
    fetchRates().then(r => {
      setRates(r);
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(r));
    });
  }, []);

  const logout = () => {
    // Clear tokens
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    
    // Redirect to login page
    navigate('/login');
  };

  return (
    <Box
      bg={scrolled ? 'primary.50' : 'transparent'}
      color="white"
      w="100%"
      h="60px"
      position="fixed"
      top="0"
      zIndex={3} // Ensure header is above sidebar and background
      boxShadow={scrolled ? 'sm' : 'none'}
      transition="background 0.3s, box-shadow 0.3s"
    >
      <Flex justify="space-between" align="center" h="100%" px={{ base: 2, md: 4 }}>
        {/* Left side: mobile menu + logo */}
        <Flex align="center" gap={2}>
          {showMenuButton && (
            <IconButton
              aria-label="Open navigation menu"
              icon={<FaBars />}
              onClick={onMenuClick}
              display={{ base: 'inline-flex', md: 'none' }}
              variant="ghost"
              colorScheme="whiteAlpha"
            />
          )}
          {/* Company Logo */}
          <Image
            src="/agro-center.svg"
            alt="Kovel Agro Logo"
            h={{ base: '52px', md: '72px' }}
            display="block"
          />
        </Flex>
        {/* Currency rates (НБУ official) */}
        <Tooltip
          label={rates.date ? `Офіційний курс НБУ · ${rates.date}` : 'Офіційний курс НБУ'}
          placement="bottom" hasArrow fontSize="xs"
        >
          <HStack
            spacing={3}
            display={{ base: 'none', md: 'flex' }}
            px={3} py={1}
            borderRadius="lg"
            bg="whiteAlpha.100"
            border="1px solid"
            borderColor="whiteAlpha.200"
            cursor="default"
          >
            <HStack spacing={1}>
              <Text fontSize="xs" color={rateLabelColor} fontWeight="medium">USD</Text>
              <Text fontSize="sm" fontWeight="bold" color={rateColor}>
                {rates.usd ? `₴${rates.usd}` : 'N/A'}
              </Text>
            </HStack>
            <Box w="1px" h="14px" bg="whiteAlpha.300" />
            <HStack spacing={1}>
              <Text fontSize="xs" color={rateLabelColor} fontWeight="medium">EUR</Text>
              <Text fontSize="sm" fontWeight="bold" color={rateColor}>
                {rates.eur ? `₴${rates.eur}` : 'N/A'}
              </Text>
            </HStack>
          </HStack>
        </Tooltip>

        {/* Profile Actions */}
        <Flex align="center" gap="4">
          <Button
            variant="ghost"
            colorScheme="whiteAlpha"
            leftIcon={<Icon as={FaUser} />}
            display={{ base: 'none', md: 'inline-flex' }}
            onClick={onOpen}
          >
            Профіль
          </Button>
          <Button
            onClick={logout}
            variant="ghost"
            colorScheme="whiteAlpha"
            leftIcon={<Icon as={FaSignOutAlt} />}
          >
            Вийти
          </Button>
        </Flex>
      </Flex>
      <ChangePasswordModal isOpen={isOpen} onClose={onClose} />
    </Box>
  );
};

export default Header;
